import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Flame, Wallet, Bell, Search, Plus, LogOut, Gem, Battery, RefreshCw 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApp } from "../../contexts/AppContext";
import { BURNZ_LOGO } from "../../constants";

export const TopHeader = ({ user }) => {
  const { handleLogout, handleDisconnect, handleReconnect, setWalletModalOpen } = useApp();
  
  return (
    <header className="h-14 lg:h-16 bg-[#0D0D0D] border-b border-white/5 flex items-center justify-between px-3 lg:px-6 sticky top-0 z-50">
      {/* Logo - Simplified on mobile */}
      <Link to="/" className="flex items-center gap-2">
        <img src={BURNZ_LOGO} alt="BURNZ" className="h-8 lg:h-10 w-auto" />
        <div className="hidden sm:block">
          <span className="text-lg lg:text-xl font-bold text-white">BURNZ</span>
          <span className="ml-1 text-[9px] lg:text-[10px] bg-purple-500 text-white px-1.5 py-0.5 rounded font-semibold">UNION</span>
        </div>
        <Badge className="hidden md:flex ml-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0 text-[10px] px-2 py-0.5 animate-pulse">
          BETA
        </Badge>
      </Link>

      {/* Search - Hidden on mobile */}
      <div className="hidden lg:block flex-1 max-w-md mx-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input placeholder="Search pools, quests..." className="w-full bg-[#1A1A1A] border-white/10 pl-10 h-10 text-white placeholder:text-white/30 focus:border-purple-500/50" />
        </div>
      </div>

      <div className="flex items-center gap-2 lg:gap-4">
        {user ? (
          <>
            {/* Stats - Simplified on mobile */}
            <div className="hidden md:flex items-center gap-2">
              <Gem className="w-4 lg:w-5 h-4 lg:h-5 text-cyan-400" />
              <span className="text-cyan-400 font-bold text-sm lg:text-base">{user?.diamonds || 44}</span>
            </div>

            <div className="flex items-center gap-1.5 lg:gap-2">
              <Flame className="w-4 lg:w-5 h-4 lg:h-5 text-orange-500" />
              <span className="text-orange-400 font-bold text-sm lg:text-base">{user?.burnz_tokens || 850}</span>
              <button className="hidden lg:flex w-5 h-5 rounded bg-white/10 items-center justify-center hover:bg-white/20">
                <Plus className="w-3 h-3 text-white" />
              </button>
            </div>

            {/* Energy Bar - Hidden on mobile (shown in MobileNav) */}
            <div className="hidden lg:flex items-center gap-2 bg-[#1A1A1A] rounded-lg px-3 py-1.5">
              <Battery className="w-4 h-4 text-purple-400" />
              <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{ width: `${((user?.energy || 180) / (user?.max_energy || 200)) * 100}%` }} />
              </div>
              <span className="text-white/70 text-sm font-mono">{user?.energy || 180}/{user?.max_energy || 200}</span>
            </div>

            {/* Notifications */}
            <button className="w-8 h-8 lg:w-9 lg:h-9 rounded-lg bg-[#1A1A1A] flex items-center justify-center hover:bg-white/10 relative">
              <Bell className="w-4 lg:w-5 h-4 lg:h-5 text-white/50" />
              {user?.notifications > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">{user.notifications}</span>
              )}
            </button>

            {/* Profile Avatar with Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="focus:outline-none">
                  <Avatar className="w-8 h-8 lg:w-10 lg:h-10 border-2 border-purple-500/50 cursor-pointer hover:border-purple-400 transition-colors">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-purple-500 text-sm lg:text-base">
                      {user?.wallet_address?.slice(5, 7)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#1A1A1A] border-white/10">
                <div className="px-3 py-2 border-b border-white/10">
                  <p className="text-sm text-white font-medium">Connected Wallet</p>
                  <p className="text-xs text-white/50 font-mono truncate">
                    {user?.wallet_address?.slice(0, 12)}...{user?.wallet_address?.slice(-8)}
                  </p>
                  {user?.is_superadmin && (
                    <Badge className="mt-1 bg-yellow-500/20 text-yellow-400 border-0 text-[10px]">
                      SUPER ADMIN
                    </Badge>
                  )}
                </div>
                <Link to="/app/profile">
                  <DropdownMenuItem className="text-white/70 hover:text-white hover:bg-white/10 cursor-pointer">
                    <Wallet className="w-4 h-4 mr-2" />
                    View Profile
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem 
                  onClick={handleReconnect}
                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 cursor-pointer"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reconnect Wallet
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleDisconnect}
                  className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Disconnect
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <>
            <Badge className="hidden sm:flex bg-yellow-500/20 text-yellow-400 border-0 text-xs">Preview Mode</Badge>
            <Button onClick={() => setWalletModalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white text-sm h-9 px-3 lg:h-10 lg:px-4">
              <Wallet className="w-4 h-4 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Connect Wallet</span>
              <span className="sm:hidden">Connect</span>
            </Button>
          </>
        )}
      </div>
    </header>
  );
};

